/**
 * =============================================================================
 * Hook: useAffiliatePortalRealtime
 * Versão: 2.0.0 (Server-side hardening)
 *
 * Mudanças v2.0.0:
 *   - O hook NÃO lê mais campos sensíveis (reward_value, status, payout_id)
 *     do payload do Realtime. Usa o evento APENAS como gatilho para
 *     invalidar o cache e forçar refetch das RPCs autoritativas.
 *   - Dados financeiros e leads continuam mascarados/recalculados no servidor.
 *
 * Toasts contextuais:
 *   - Mantemos toasts informativos baseados em `payload.new.status` e
 *     `payload.new.title` (notificações), porque são metadata leve que o
 *     próprio canal já entrega (não há vazamento adicional). Os VALORES
 *     financeiros nunca aparecem no toast.
 * =============================================================================
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const log = createLogger('useAffiliatePortalRealtime');

const INVALIDATION_DEBOUNCE_MS = 300;

/**
 * Estilo P&B (preto sobre branco) para toasts do portal do afiliado.
 *
 * Removemos as cores temáticas do Sonner (info=azul, success=verde,
 * warning=amarelo, error=vermelho) para um visual editorial monocromático
 * — somente preto, branco e cinzas neutros. Aplicado via `unstyled` +
 * `classNames` para sobrescrever totalmente o estilo padrão sem afetar
 * outros toasts da aplicação (apenas os disparados por este hook).
 */
const AFFILIATE_TOAST_OPTIONS = {
  unstyled: true,
  classNames: {
    toast:
      'group flex items-start gap-3 w-full rounded-xl border border-black/15 bg-white text-black shadow-[0_8px_24px_-12px_rgba(0,0,0,0.25)] p-4',
    title: 'text-sm font-semibold text-black',
    description: 'text-sm text-neutral-600 mt-0.5',
    icon: 'text-black',
    closeButton:
      'bg-white border border-black/15 text-black hover:bg-neutral-100',
  },
} as const;

/**
 * Tabelas escutadas → query keys que devem ser invalidadas.
 * A overview é sempre invalidada porque é a fonte única de verdade.
 */
const QUERY_KEYS_BY_TABLE: Record<string, string[][]> = {
  affiliate_notifications: [
    ['affiliate-notifications'],
    ['affiliate-home-overview'],
  ],
  affiliate_referrals: [
    ['affiliate-referrals'],
    ['affiliate-home-overview'],
  ],
  affiliate_commissions: [
    ['affiliate-commissions'],
    ['affiliate-payouts'],
    ['affiliate-home-overview'],
  ],
  affiliate_payouts: [
    ['affiliate-payouts'],
    ['affiliate-home-overview'],
  ],
};

export function useAffiliatePortalRealtime(): void {
  const { affiliate, ready } = useAffiliateAuth();
  const queryClient = useQueryClient();

  const pendingKeysRef = useRef<Set<string>>(new Set());
  const debounceTimerRef = useRef<number | null>(null);

  const scheduleInvalidate = (keys: string[][]) => {
    for (const key of keys) {
      pendingKeysRef.current.add(JSON.stringify(key));
    }
    if (debounceTimerRef.current !== null) return;

    debounceTimerRef.current = window.setTimeout(() => {
      const toInvalidate = Array.from(pendingKeysRef.current).map(
        (s) => JSON.parse(s) as string[],
      );
      pendingKeysRef.current.clear();
      debounceTimerRef.current = null;

      for (const key of toInvalidate) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      log.debug('Queries invalidadas', { count: toInvalidate.length });
    }, INVALIDATION_DEBOUNCE_MS);
  };

  useEffect(() => {
    if (!ready || !affiliate?.id) return;

    const affiliateId = affiliate.id;
    log.info('Iniciando subscriptions Realtime do portal', { affiliateId });

    const channel: RealtimeChannel = supabase
      .channel(`affiliate-portal:${affiliateId}`)

      // Notificações: usa apenas title/type para o toast (metadata leve).
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'affiliate_notifications',
          filter: `affiliate_id=eq.${affiliateId}`,
        },
        (payload) => {
          const row = payload.new as { title?: string; message?: string | null; type?: string };
          scheduleInvalidate(QUERY_KEYS_BY_TABLE.affiliate_notifications);

          const title = row.title || 'Nova notificação';
          const description = row.message || undefined;
          // Visual P&B unificado — sem variantes coloridas (info/success/etc.).
          toast(title, { description, ...AFFILIATE_TOAST_OPTIONS });
        },
      )

      // Referrals (INSERT/UPDATE): apenas invalida cache.
      // O toast informativo agora é gerado pela trigger
      // `tg_fn_affiliate_referral_notify`, que grava em
      // `affiliate_notifications` — e o handler acima já dispara o toast
      // contextual (criada/convertida/cancelada). Evitamos toast duplicado.
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'affiliate_referrals',
          filter: `affiliate_id=eq.${affiliateId}`,
        },
        () => {
          scheduleInvalidate(QUERY_KEYS_BY_TABLE.affiliate_referrals);
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'affiliate_referrals',
          filter: `affiliate_id=eq.${affiliateId}`,
        },
        () => {
          scheduleInvalidate(QUERY_KEYS_BY_TABLE.affiliate_referrals);
        },
      )

      // Comissões (INSERT/UPDATE): apenas invalida cache.
      // Toasts agora vêm via triggers `tg_fn_affiliate_commission_notify`
      // (INSERT) e `tg_fn_affiliate_commission_status_notify` (UPDATE),
      // que cobrem pendente/aprovada/paga com mensagem específica.
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'affiliate_commissions',
          filter: `affiliate_id=eq.${affiliateId}`,
        },
        () => {
          scheduleInvalidate(QUERY_KEYS_BY_TABLE.affiliate_commissions);
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'affiliate_commissions',
          filter: `affiliate_id=eq.${affiliateId}`,
        },
        () => {
          scheduleInvalidate(QUERY_KEYS_BY_TABLE.affiliate_commissions);
        },
      )

      // Payouts (INSERT/UPDATE): invalida overview + lista de saques.
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliate_payouts',
          filter: `affiliate_id=eq.${affiliateId}`,
        },
        () => {
          scheduleInvalidate(QUERY_KEYS_BY_TABLE.affiliate_payouts);
        },
      )

      .subscribe((status) => {
        log.debug('Status do canal Realtime', { status });
      });

    return () => {
      log.info('Encerrando subscriptions Realtime do portal');
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingKeysRef.current.clear();
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, affiliate?.id]);
}
